import { useEffect, useRef, useState } from 'react';
import clsx from 'clsx';
import { useMediaQuery } from '@ag1/ui';
import {
  ImageContentHighlight as ImageContentHighlightProps,
  SanityKeyed
} from 'types/studio/studio';
import { StudioBaseImageV2 } from 'shared/studio/StudioBaseImage/StudioBaseImage';
import { CommonSectionProps } from '../types';
import ImageContentHighlightHeading from './ImageContentHighlightHeading/ImageContentHighlightHeading';
import ImageContentHighlightBody from './ImageContentHighlightBody/ImageContentHighlightBody';
import { getBgColorClassName } from './utils';
import * as styles from './ImageContentHighlight.css';

const ImageContentHighlight = (
  props: SanityKeyed<ImageContentHighlightProps> & CommonSectionProps
) => {
  const {
    _type: sectionType,
    _key: key,
    appearance,
    backgroundLight,
    backgroundDark,
    backgroundColorIsNarrow,
    backgroundColor,
    mainImage,
    isFullScreenBg: isFullScreenBackgroundImage,
    contentPosition,
    complementaryImage,
    complementaryImageOnMobile,
    superHeadline,
    headline,
    subtitle,
    bodyTitle,
    bodyText,
    cards,
    unifiedButton,
    showAdditionalButton,
    additionalButton
  } = props;
  const [headerHeight, setHeaderHeight] = useState(0);
  const [contentOffsetHeight, setContentOffsetHeight] = useState(0);
  const aboveMedium = useMediaQuery('m');
  // This is a special edge case for "desktop" breakpoint starting at 1025px as per Figma designs: https://www.figma.com/design/r2PRPDVKWKJYPyzevukqz7/%F0%9F%95%B9%EF%B8%8F-Growth-components?node-id=16730-44572&m=dev.
  const aboveDesktop = useMediaQuery(1025);
  const contentRef = useRef<HTMLDivElement>(null);
  const imageWrapperRef = useRef<HTMLDivElement>(null);

  const shouldAlignContentToRight = contentPosition === 'right';
  const isDarkMode = appearance === 'dark';

  const hasComplementaryImage = complementaryImage && complementaryImage.image;
  const showComplementaryImageOnMobileOnly = complementaryImageOnMobile && !aboveDesktop;
  const shouldShowComplementaryImage =
    hasComplementaryImage && (!complementaryImageOnMobile || showComplementaryImageOnMobileOnly);

  const hasCards = cards && cards.length > 0;
  const isFullScreenBg =
    isFullScreenBackgroundImage ||
    (!aboveDesktop && !(hasComplementaryImage || showComplementaryImageOnMobileOnly));

  useEffect(() => {
    setHeaderHeight(document.querySelector<HTMLElement>('#nav__header')?.offsetHeight || 0);
  }, [aboveMedium]);

  // Observe contentHeight changes in the contentRef element
  // Ideally it would just render the contentRef element at the correct offset on first render
  // Likely unavoidable with SSR
  useEffect(() => {
    if (!contentRef.current) return;

    let lastHeight = 0;

    const updateHeight = () => {
      if (contentRef.current) {
        const height = contentRef.current.offsetHeight;
        if (height !== lastHeight) {
          lastHeight = height;
          setContentOffsetHeight(height);
        }
      }
    };

    // Initial measurement
    updateHeight();

    const resizeObserver = new ResizeObserver(updateHeight);
    resizeObserver.observe(contentRef.current);

    return () => resizeObserver.disconnect();
  }, []);

  return (
    <div
      id={key}
      className={clsx(
        styles.root,
        shouldAlignContentToRight && styles.rootShouldAlignContentToRight,
        isDarkMode && styles.rootDarkMode,
        !backgroundColorIsNarrow &&
          styles[getBgColorClassName({ isDarkMode, backgroundDark, backgroundLight })]
      )}
      style={
        {
          ...(backgroundColor
            ? { backgroundColor: backgroundColor.value, backgroundImage: 'initial' }
            : {}),
          '--header-height': headerHeight > 0 ? `${headerHeight}px` : '0px',
          '--viewport-height-minus-header':
            headerHeight > 0 ? 'calc(100vh - var(--header-height))' : '100vh',
          '--content-height':
            contentOffsetHeight > 0
              ? `${contentOffsetHeight}px`
              : 'var(--viewport-height-minus-header)',
          '--image-wrapper-height': imageWrapperRef.current?.offsetHeight
            ? `${imageWrapperRef.current.offsetHeight}px`
            : 'initial'
        } as React.CSSProperties
      }>
      <div
        className={clsx(
          shouldAlignContentToRight && styles.contentWrapperShouldAlignContentToRight,
          isFullScreenBg && styles.contentWrapperFullScreenBg,
          isFullScreenBg && hasCards && styles.contentWrapperFullScreenBgAndHasCards,
          backgroundColorIsNarrow &&
            styles[getBgColorClassName({ isDarkMode, backgroundDark, backgroundLight })]
        )}>
        <div
          className={clsx(
            shouldShowComplementaryImage && styles.complementaryImageAndContentWrapper
          )}
          ref={imageWrapperRef}>
          {shouldShowComplementaryImage && (
            <StudioBaseImageV2
              image={complementaryImage.image!}
              mobileImage={complementaryImage.mobileImage}
              withMobileImage={complementaryImage.withMobileImage}
              tabletImage={complementaryImage.tabletImage}
              className={styles.complementaryImage}
              mobileFullQuality
            />
          )}
          <div
            className={clsx(
              styles.content,
              shouldAlignContentToRight && styles.contentShouldAlignContentToRight,
              shouldShowComplementaryImage && styles.contentHasComplementaryImage,
              isFullScreenBg && styles.contentFullScreenBg
            )}
            ref={contentRef}>
            <ImageContentHighlightHeading
              superHeadline={superHeadline}
              headline={headline}
              subtitle={subtitle}
            />
            <ImageContentHighlightBody
              sectionType={sectionType}
              bodyTitle={bodyTitle}
              bodyText={bodyText}
              cards={cards}
              unifiedButton={unifiedButton}
              showAdditionalButton={showAdditionalButton}
              additionalButton={additionalButton}
              isDarkMode={isDarkMode}
            />
          </div>
        </div>
      </div>

      {mainImage && mainImage.image && (
        <StudioBaseImageV2
          image={mainImage.image}
          mobileImage={mainImage.mobileImage}
          withMobileImage={mainImage.withMobileImage}
          tabletImage={mainImage.tabletImage}
          className={clsx(
            styles.image,
            shouldShowComplementaryImage && styles.imageHasComplementaryImage,
            isFullScreenBg && styles.imageFullScreenBg,
            isFullScreenBg && hasCards && styles.imageFullScreenBgAndHasCards
          )}
          mobileFullQuality
        />
      )}
    </div>
  );
};

export default ImageContentHighlight;
